import {copilot} from '@cl/shared';
import {Chat, ChatState} from 'common/src/modules/shared/copilot/copilot.types';
import chat from 'common/src/modules/shared/copilot/utils/chat';
import formatCancellationSignalName from 'common/src/modules/shared/copilot/utils/formatCancellationSignalName';
import {randomKey} from 'common/src/utils/commonUtils';
import signal from 'common/src/utils/pubsub/signal';
import {DateTime} from 'luxon';
import {ChatType} from '../../../copilot.enums';
import chatState from '../../../utils/creators/chatState';
import {PersistedChatSnapshot} from '../../persistence.types';

export default function convertSnapshotToChat(
  snapshot: PersistedChatSnapshot,
  options: {preserveChatIds: boolean},
): {
  restoredRuntimeChat: Chat;
  restoredReduxState: ChatState;
} {
  const {preserveChatIds} = options;
  const emptyChat = chat(copilot.chatConfigForChatType(snapshot.chatType));
  const emptySession = emptyChat.agentSession;
  const chatId = preserveChatIds ? snapshot.chatId : emptyChat.id;
  const emptyReduxState = chatState({
    chatId,
    type: snapshot.chatType || ChatType.General,
    isPlanModeEnabled: snapshot.redux.isPlanModeEnabled,
  });

  if (!emptySession) {
    throw new Error('The newly created chat has no agent session. The chat cannot be restored.');
  }

  const {runtime, redux} = snapshot;

  const restoredRuntimeChat: Chat = {
    id: chatId,
    type: emptyChat.type,
    agents: emptyChat.agents,
    fallbackAgent: emptyChat.fallbackAgent,
    isPlanModeEnabled: emptyChat.isPlanModeEnabled,
    abortController: emptyChat.abortController,
    draftFiles: emptyChat.draftFiles,
    currentInteractionId: emptyChat.currentInteractionId,
    cancellationCompleteSignal: preserveChatIds
      ? signal(formatCancellationSignalName(chatId))
      : emptyChat.cancellationCompleteSignal,
    blockingWidgetResolvers: emptyChat.blockingWidgetResolvers,

    agentSession: {
      ...emptySession,
      memory: runtime.agentSession.memory,
      summary: runtime.agentSession.summary,
      createdAtTimestamp: runtime.agentSession.createdAtTimestamp,
    },
    blackboard: {
      chatHistory: runtime.blackboard.chatHistory.map((entry) => ({
        sessionStartedAt: new Date(entry.sessionStartedAt),
        sessionEndedAt: new Date(entry.sessionEndedAt),
        sessionId: entry.sessionId,
        agentName: entry.agentName,
        facts: entry.facts,
      })),
      plan: runtime.blackboard.plan
        ? {
            id: randomKey(),
            createdAt: DateTime.fromISO(runtime.blackboard.plan.createdAt),
            ownerAgentName: runtime.blackboard.plan.ownerAgentName,
            goal: runtime.blackboard.plan.goal,
            steps: runtime.blackboard.plan.steps,
            skillNames: runtime.blackboard.plan.skillNames,
            resources: runtime.blackboard.plan.resources,
            issues: runtime.blackboard.plan.issues,
            openQuestions: runtime.blackboard.plan.openQuestions,
            userInteractionStatus: runtime.blackboard.plan.userInteractionStatus,
          }
        : null,
    },
    files: runtime.files,
  };

  const restoredReduxState: ChatState = {
    isPlanModeEnabled: emptyReduxState.isPlanModeEnabled,
    modalWidget: emptyReduxState.modalWidget,
    blockingChatItemId: emptyReduxState.blockingChatItemId,
    contextWindowUsage: redux.contextWindowUsage,

    id: restoredRuntimeChat.id,
    type: redux.type,
    title: redux.title,
    status: redux.status,
    chatItems: redux.chatItems,
    informationText: redux.informationText,
    draftMessage: redux.draftMessage,
    plan: redux.plan
      ? {
          id: redux.plan.id,
          createdAt: DateTime.fromISO(redux.plan.createdAt),
          ownerAgentName: redux.plan.ownerAgentName,
          goal: redux.plan.goal,
          steps: redux.plan.steps,
          status: redux.plan.status,
          skillNames: redux.plan.skillNames,
          resources: redux.plan.resources,
          issues: redux.plan.issues,
          openQuestions: redux.plan.openQuestions,
          userInteractionStatus: redux.plan.userInteractionStatus,
        }
      : null,
    isPlanModeToggleOn: redux.isPlanModeToggleOn,
    planCollapseState: redux.planCollapseState,
    lastUpdated: redux.lastUpdated,
  };

  return {restoredRuntimeChat, restoredReduxState};
}
